/**
 * Collapsible transcript inspector for the admin video page.
 *
 * Lazy-loads on open so we don't round-trip 30k-char transcripts for
 * admins who never click through. Renders the text with minimal but
 * deliberate formatting: we collapse stretches of repeated whitespace,
 * split on existing line breaks, and group short utterances into
 * paragraph-size blocks so auto-caption streams (which usually arrive
 * as one unbroken blob) are actually readable.
 */
import React, {useMemo, useState} from 'react';
import {
  ApolloClient,
  NormalizedCacheObject,
} from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArticleIcon from '@mui/icons-material/Article';
import {pcolors, cardSx, sectionLabelSx} from '../../theme/planapusTheme';
import {useGetAdminVideoTranscript} from '../../api/graphql_video_transcript';
import {LanguageChip} from './LanguageChip';

interface Props {
  videoId: string;
  client: ApolloClient<NormalizedCacheObject>;
  /** Total video length in seconds — shown alongside the transcript stats. */
  durationSec?: number | null;
}

/**
 * Split a transcript string into readable paragraph-size blocks.
 *
 * YouTube auto-captions usually come as one giant line with erratic
 * spacing. If we just dump that, it's an unreadable wall. This
 * grouping picks natural break points:
 *   - Existing \n boundaries (kept as-is — human-authored SRTs respect these).
 *   - Sentence ends (`. `, `? `, `! `) — so caption streams with punctuation
 *     break into sentence-sized chunks.
 *   - Hard cap around 400 chars so a single punctuation-free line doesn't
 *     collapse into one giant paragraph.
 *
 * We then group runs of short lines (< 60 chars) together because
 * caption streams frequently come pre-split on timing boundaries that
 * don't match linguistic boundaries; merging them gives a paragraph.
 */
function formatTranscriptParagraphs(raw: string): string[] {
  const norm = raw.replace(/\r\n?/g, '\n').replace(/[ \t]+/g, ' ').trim();
  if (!norm) return [];

  // First: split on existing line breaks.
  const rawLines = norm.split('\n').map(l => l.trim()).filter(Boolean);

  // If the transcript is essentially one line, try to break it at
  // sentence endings before paragraph-grouping.
  const lines: string[] = [];
  for (const line of rawLines) {
    if (line.length <= 400) {
      lines.push(line);
      continue;
    }
    // Sentence-boundary split. Keep the terminator glued to the
    // previous sentence so the output reads naturally.
    const pieces = line.split(/(?<=[.!?])\s+(?=[A-Z"'([])/g);
    for (const p of pieces) {
      // Hard cap — if still too long, chunk at word boundaries so no
      // single paragraph exceeds ~400 chars.
      if (p.length <= 400) {
        lines.push(p);
      } else {
        let rest = p;
        while (rest.length > 400) {
          const cut = rest.lastIndexOf(' ', 400);
          const at = cut > 200 ? cut : 400;
          lines.push(rest.slice(0, at));
          rest = rest.slice(at).trimStart();
        }
        if (rest) lines.push(rest);
      }
    }
  }

  // Second: group short consecutive lines into paragraphs. Many
  // auto-caption streams come as one-line-per-caption-segment (5-10
  // words each); grouping ~3-6 of them reads like a paragraph.
  const paragraphs: string[] = [];
  let buf: string[] = [];
  let bufLen = 0;
  const flush = () => {
    if (buf.length) {
      paragraphs.push(buf.join(' '));
      buf = [];
      bufLen = 0;
    }
  };
  for (const line of lines) {
    if (line.length >= 120) {
      // Long-enough line stands on its own.
      flush();
      paragraphs.push(line);
      continue;
    }
    buf.push(line);
    bufLen += line.length + 1;
    // Flush when paragraph gets hefty or when a line ends with
    // terminal punctuation (natural paragraph boundary).
    if (bufLen > 360 || /[.!?]$/.test(line)) {
      flush();
    }
  }
  flush();
  return paragraphs.filter(Boolean);
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

export function TranscriptPanel({videoId, client, durationSec}: Props) {
  const [open, setOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const {load, data, loading, error} = useGetAdminVideoTranscript(client);

  const t = data?.getAdminVideoTranscript;
  const body = t?.transcript ?? '';
  const lang = t?.language ?? null;
  const charLen = body.length;
  const density = durationSec && durationSec > 0 ? (charLen / (durationSec / 60)) : null;
  const paragraphs = useMemo(() => formatTranscriptParagraphs(body), [body]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !hasLoaded) {
      load({variables: {videoId}});
      setHasLoaded(true);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard permissions can vary; silent fail is fine here.
    }
  };

  return (
    <Paper sx={{...cardSx, p: 2, mb: 3}}>
      <Box
        sx={{display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer'}}
        onClick={handleToggle}
      >
        <ArticleIcon sx={{fontSize: 18, color: pcolors.textSecondary}} />
        <Typography sx={sectionLabelSx}>Transcript</Typography>
        {hasLoaded && t && (
          <Typography
            sx={{fontSize: 11, color: pcolors.textMuted, fontVariantNumeric: 'tabular-nums'}}
          >
            {charLen.toLocaleString()} chars
            {durationSec ? ` · ${formatDuration(durationSec)}` : ''}
            {density != null ? ` · ${Math.round(density)} chars/min` : ''}
          </Typography>
        )}
        {hasLoaded && lang && <LanguageChip language={lang} showConfidence />}
        <Box sx={{flex: 1}} />
        {open && t?.transcript && (
          <Tooltip title={copied ? 'Copied' : 'Copy raw transcript'}>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleCopy();
              }}
            >
              <ContentCopyIcon sx={{fontSize: 16}} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" aria-label={open ? 'Collapse transcript' : 'Expand transcript'}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {open && (
        <Box sx={{mt: 1.5}}>
          {loading && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 2}}>
              <CircularProgress size={16} />
              <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>
                Loading transcript…
              </Typography>
            </Box>
          )}
          {error && (
            <Typography sx={{fontSize: 12, color: pcolors.red[700]}}>
              Failed to load transcript: {error.message}
            </Typography>
          )}
          {hasLoaded && !loading && !error && !body && (
            <Box sx={{py: 2}}>
              <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>
                No transcript cached for this video.
              </Typography>
              <Typography sx={{fontSize: 11, color: pcolors.textMuted, mt: 0.5}}>
                The scoring pipeline ran against metadata only — specialists
                treated educational claims as unverified per the
                <code>[DATA: metadata-only]</code> rule.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => load({variables: {videoId}})}
                sx={{mt: 1}}
              >
                Retry
              </Button>
            </Box>
          )}
          {hasLoaded && !loading && body && (
            <Box
              sx={{
                maxHeight: 520,
                overflowY: 'auto',
                px: 1.5,
                py: 1,
                bgcolor: pcolors.bg,
                border: `1px solid ${pcolors.borderLight}`,
                borderRadius: 1,
              }}
            >
              {paragraphs.map((p, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: pcolors.textPrimary,
                    mb: 1.25,
                    '&:last-of-type': {mb: 0},
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {p}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}
